// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";
import { CalendarDays, Mail, MessageSquare, TrendingUp, Users, RefreshCw } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

interface CommunicationStat {
  type: 'email' | 'sms';
  status: string;
  count: number;
  date?: string;
}

interface DashboardMetrics {
  totalSent: number;
  emailsSent: number;
  smsSent: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
}

interface TemplateStats {
  template_name: string;
  email_count: number;
  sms_count: number;
  total_count: number;
  sent_count: number;
  failed_count: number;
}

const CommunicationAnalyticsTab: React.FC = () => {
  console.log("CommunicationAnalyticsTab rendering...");
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalSent: 0,
    emailsSent: 0,
    smsSent: 0,
    deliveryRate: 0,
    openRate: 0,
    clickRate: 0,
  });
  const [dailyStats, setDailyStats] = useState<any[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<any[]>([]);
  const [typeDistribution, setTypeDistribution] = useState<any[]>([]);
  const [templateStats, setTemplateStats] = useState<TemplateStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("7");

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      
      const daysAgo = parseInt(timeRange);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);
      
      // Fetch communication logs
      const { data: logs, error: logsError } = await supabase
        .from('communication_logs')
        .select('*')
        .gte('created_at', startDate.toISOString());

      if (logsError) throw logsError;

      // Calculate metrics
      const totalSent = logs?.filter(log => log.status === 'sent').length || 0;
      const emailsSent = logs?.filter(log => log.type === 'email' && log.status === 'sent').length || 0;
      const smsSent = logs?.filter(log => log.type === 'sms' && log.status === 'sent').length || 0;
      const delivered = logs?.filter(log => log.delivered_at).length || 0;
      const opened = logs?.filter(log => log.opened_at).length || 0;
      const clicked = logs?.filter(log => log.clicked_at).length || 0;

      setMetrics({
        totalSent,
        emailsSent,
        smsSent,
        deliveryRate: totalSent > 0 ? Math.round((delivered / totalSent) * 100) : 0,
        openRate: emailsSent > 0 ? Math.round((opened / emailsSent) * 100) : 0,
        clickRate: emailsSent > 0 ? Math.round((clicked / emailsSent) * 100) : 0,
      });

      // Group by date for daily stats
      const dailyData = logs?.reduce((acc: any, log) => {
        const date = new Date(log.created_at).toLocaleDateString();
        if (!acc[date]) {
          acc[date] = { date, emails: 0, sms: 0, total: 0 };
        }
        if (log.status === 'sent') {
          acc[date].total++;
          if (log.type === 'email') acc[date].emails++;
          if (log.type === 'sms') acc[date].sms++;
        }
        return acc;
      }, {});

      setDailyStats(Object.values(dailyData || {}));

      // Status distribution
      const statusData = logs?.reduce((acc: any, log) => {
        acc[log.status] = (acc[log.status] || 0) + 1;
        return acc;
      }, {});

      setStatusDistribution(
        Object.entries(statusData || {}).map(([status, count]) => ({
          name: status,
          value: count,
        }))
      );

      // Type distribution
      const typeData = logs?.reduce((acc: any, log) => {
        acc[log.type] = (acc[log.type] || 0) + 1;
        return acc;
      }, {});

      setTypeDistribution(
        Object.entries(typeData || {}).map(([type, count]) => ({
          name: type,
          value: count,
        }))
      );

      // Template statistics
      const templateData = logs?.reduce((acc: any, log) => {
        const templateName = log.template_name || 
                            (log.metadata?.template_name) || 
                            log.subject || 
                            'Unknown Template';
        
        if (!acc[templateName]) {
          acc[templateName] = {
            template_name: templateName,
            email_count: 0,
            sms_count: 0,
            total_count: 0,
            sent_count: 0,
            failed_count: 0,
          };
        }
        
        acc[templateName].total_count++;
        if (log.type === 'email') acc[templateName].email_count++;
        if (log.type === 'sms') acc[templateName].sms_count++;
        if (log.status === 'sent') acc[templateName].sent_count++;
        if (log.status === 'failed') acc[templateName].failed_count++;
        
        return acc;
      }, {});

      setTemplateStats(
        (Object.values(templateData || {}) as TemplateStats[])
          .sort((a, b) => b.total_count - a.total_count)
      );

    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const COLORS = ['#002E62', '#BC8D09', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading Analytics...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Communication Analytics</h2>
          <p className="text-muted-foreground">
            Track email and SMS communication performance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              <div className="ml-2">
                <p className="text-sm font-medium leading-none">Total Sent</p>
                <p className="text-2xl font-bold">{metrics.totalSent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div className="ml-2">
                <p className="text-sm font-medium leading-none">Emails Sent</p>
                <p className="text-2xl font-bold">{metrics.emailsSent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <div className="ml-2">
                <p className="text-sm font-medium leading-none">SMS Sent</p>
                <p className="text-2xl font-bold">{metrics.smsSent}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="ml-2">
                <p className="text-sm font-medium leading-none">Delivery Rate</p>
                <p className="text-2xl font-bold">{metrics.deliveryRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="ml-2">
                <p className="text-sm font-medium leading-none">Open Rate</p>
                <p className="text-2xl font-bold">{metrics.openRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="ml-2">
                <p className="text-sm font-medium leading-none">Click Rate</p>
                <p className="text-2xl font-bold">{metrics.clickRate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Daily Communication Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="emails" stackId="a" fill="#002E62" name="Emails" />
                <Bar dataKey="sms" stackId="a" fill="#BC8D09" name="SMS" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Communication Types</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={typeDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {typeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Delivery Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusDistribution}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Communication Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#002E62" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Template Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle>Messages Sent by Template</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4">Template Name</th>
                  <th className="text-right py-2 px-4">Total</th>
                  <th className="text-right py-2 px-4">Emails</th>
                  <th className="text-right py-2 px-4">SMS</th>
                  <th className="text-right py-2 px-4">Sent</th>
                  <th className="text-right py-2 px-4">Failed</th>
                  <th className="text-right py-2 px-4">Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {templateStats.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-muted-foreground">
                      No template data available for this time period
                    </td>
                  </tr>
                ) : (
                  templateStats.map((stat, index) => (
                    <tr key={index} className="border-b hover:bg-accent/50">
                      <td className="py-2 px-4 font-medium">{stat.template_name}</td>
                      <td className="text-right py-2 px-4">{stat.total_count}</td>
                      <td className="text-right py-2 px-4">{stat.email_count}</td>
                      <td className="text-right py-2 px-4">{stat.sms_count}</td>
                      <td className="text-right py-2 px-4 text-green-600">{stat.sent_count}</td>
                      <td className="text-right py-2 px-4 text-red-600">{stat.failed_count}</td>
                      <td className="text-right py-2 px-4">
                        <Badge variant={stat.total_count > 0 && (stat.sent_count / stat.total_count) >= 0.95 ? "default" : "secondary"}>
                          {stat.total_count > 0 ? Math.round((stat.sent_count / stat.total_count) * 100) : 0}%
                        </Badge>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CommunicationAnalyticsTab;